/**
 * PageNavigation Component
 *
 * Provides page navigation for PDF documents:
 * - Previous/Next page buttons
 * - Page number input
 * - Total pages display
 * - Keyboard navigation support
 */

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';

interface PageNavigationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function PageNavigation({ currentPage, totalPages, onPageChange }: PageNavigationProps) {
  const [inputValue, setInputValue] = useState(currentPage.toString());

  // Sync input with current page
  useEffect(() => {
    setInputValue(currentPage.toString());
  }, [currentPage]);

  const goToPage = useCallback((page: number) => {
    const validPage = Math.max(1, Math.min(page, totalPages));
    onPageChange(validPage);
  }, [totalPages, onPageChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    const page = parseInt(inputValue, 10);
    if (!isNaN(page)) {
      goToPage(page);
    } else {
      setInputValue(currentPage.toString());
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    } else if (e.key === 'Escape') {
      setInputValue(currentPage.toString());
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case 'PageUp':
        case 'ArrowUp':
          e.preventDefault();
          goToPage(currentPage - 1);
          break;
        case 'PageDown':
        case 'ArrowDown':
          e.preventDefault();
          goToPage(currentPage + 1);
          break;
        case 'Home':
          if (e.ctrlKey) {
            e.preventDefault();
            goToPage(1);
          }
          break;
        case 'End':
          if (e.ctrlKey) {
            e.preventDefault();
            goToPage(totalPages);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, totalPages, goToPage]);

  return (
    <div className="flex items-center gap-2">
      {/* First page */}
      <button
        onClick={() => goToPage(1)}
        disabled={currentPage === 1}
        className="p-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="First page (Ctrl+Home)"
      >
        <ChevronsLeft className="w-4 h-4" />
      </button>

      {/* Previous page */}
      <button
        onClick={() => goToPage(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Previous page (Page Up)"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      {/* Page input */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          className="w-12 px-2 py-1 text-center bg-background border border-border rounded-[var(--radius-button)] text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Current page"
        />
        <span className="text-sm text-muted-foreground">
          / {totalPages}
        </span>
      </div>

      {/* Next page */}
      <button
        onClick={() => goToPage(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Next page (Page Down)"
      >
        <ChevronRight className="w-4 h-4" />
      </button>

      {/* Last page */}
      <button
        onClick={() => goToPage(totalPages)}
        disabled={currentPage === totalPages}
        className="p-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Last page (Ctrl+End)"
      >
        <ChevronsRight className="w-4 h-4" />
      </button>
    </div>
  );
}
